import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { API } from "@/App";
import { ExternalLink, Heart } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_crm-news-hub/artifacts/srv2lps9_ChatGPT%20Image%20Apr%202%2C%202026%2C%2010_47_10%20AM.png";

const TIER_CONFIG = {
  kit: { label: "Kit Partner", order: 0, size: "large" },
  official: { label: "Official Partner", order: 1, size: "large" },
  club: { label: "Club Partner", order: 2, size: "medium" },
  community: { label: "Community Partner", order: 3, size: "small" },
  custom: { label: "Partner", order: 4, size: "medium" },
};

const SponsorsShowcasePage = () => {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSponsors = async () => {
      try {
        const res = await axios.get(`${API}/sponsors/showcase`);
        setSponsors(res.data);
      } catch (err) {
        console.error("Failed to fetch sponsors:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchSponsors();
  }, []);

  const handleClick = async (sponsor) => {
    try {
      await axios.post(`${API}/sponsors/${sponsor.sponsor_id}/click`);
    } catch { /* tracking failure is non-critical */ }
    if (sponsor.website_url) {
      window.open(sponsor.website_url, "_blank", "noopener,noreferrer");
    }
  };

  // Group sponsors by tier
  const tiers = {};
  sponsors.forEach((s) => {
    const tier = s.tier || "community";
    if (!tiers[tier]) tiers[tier] = [];
    tiers[tier].push(s);
  });
  const sortedTiers = Object.entries(tiers).sort(
    ([a], [b]) => (TIER_CONFIG[a]?.order ?? 99) - (TIER_CONFIG[b]?.order ?? 99)
  );

  return (
    <div className="min-h-screen bg-[#F0F4F3]">
      <Helmet>
        <title>Our Sponsors | Commonwealth FC Youth Soccer</title>
        <meta name="description" content="Meet the businesses and organizations that support Commonwealth FC youth soccer in Charlottesville, VA. Become a sponsor and invest in local youth athletics." />
        <meta property="og:title" content="Sponsors | Commonwealth FC" />
        <meta property="og:description" content="Businesses supporting youth soccer in Charlottesville, VA." />
        <meta property="og:url" content="https://commonwealthfc.org/sponsors" />
        <link rel="canonical" href="https://commonwealthfc.org/sponsors" />
      </Helmet>

      <nav className="bg-[#0C2E3C] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Commonwealth FC" className="h-9 w-auto" />
            <span className="text-sm font-bold uppercase tracking-wider text-[#F0F4F3] hidden sm:block">Commonwealth FC</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/events" className="text-[#F0F4F3]/70 hover:text-[#C8E64E] text-sm font-medium">Events</Link>
            <Link to="/join" className="text-[#F0F4F3]/70 hover:text-[#C8E64E] text-sm font-medium">Join</Link>
            <Link to="/" className="text-[#F0F4F3]/70 hover:text-[#C8E64E] text-sm font-medium">Home</Link>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Hero */}
        <div className="text-center mb-16">
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#0C2E3C] mb-4" data-testid="sponsors-page-title">
            OUR SPONSORS
          </h1>
          <p className="text-[#6B8A8F] text-base max-w-2xl mx-auto">
            These businesses and organizations make it possible for every player in Charlottesville to access competitive soccer — regardless of what their family can afford.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0C2E3C]" />
          </div>
        ) : sponsors.length === 0 ? (
          <div className="text-center py-16">
            <Heart size={48} className="mx-auto text-[#6B8A8F]/30 mb-4" />
            <p className="text-[#6B8A8F]">Sponsor listings coming soon.</p>
          </div>
        ) : (
          <div className="space-y-16" data-testid="sponsor-tiers">
            {sortedTiers.map(([tier, tierSponsors]) => {
              const cfg = TIER_CONFIG[tier] || TIER_CONFIG.community;
              return (
                <div key={tier} data-testid={`tier-${tier}`}>
                  <h2 className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-[#0C2E3C]/40 text-center mb-8">
                    {cfg.label}s
                  </h2>
                  <div className={`grid gap-6 ${
                    cfg.size === "large" ? "grid-cols-1 sm:grid-cols-2 max-w-3xl mx-auto"
                    : cfg.size === "medium" ? "grid-cols-2 sm:grid-cols-3"
                    : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"
                  }`}>
                    {tierSponsors.map((sponsor) => (
                      <button
                        key={sponsor.sponsor_id}
                        onClick={() => handleClick(sponsor)}
                        className="group bg-white border border-[#0C2E3C]/10 p-6 text-center hover:border-[#C8E64E] hover:shadow-lg transition-all cursor-pointer"
                        data-testid={`showcase-sponsor-${sponsor.sponsor_id}`}
                      >
                        {sponsor.logo_url ? (
                          <img
                            src={sponsor.logo_url}
                            alt={sponsor.name}
                            className={`mx-auto object-contain mb-4 ${cfg.size === "large" ? "h-24" : cfg.size === "medium" ? "h-16" : "h-12"}`}
                          />
                        ) : (
                          <div className={`mx-auto bg-[#0C2E3C]/5 flex items-center justify-center mb-4 ${cfg.size === "large" ? "h-24 w-48" : "h-16 w-32"}`}>
                            <span className="text-[#0C2E3C]/30 font-bold text-lg">{sponsor.name?.charAt(0)}</span>
                          </div>
                        )}
                        <h3 className="font-bold text-sm text-[#0C2E3C] group-hover:text-[#0C2E3C]">{sponsor.name}</h3>
                        {sponsor.description && cfg.size !== "small" && (
                          <p className="text-xs text-[#6B8A8F] mt-1 line-clamp-2">{sponsor.description}</p>
                        )}
                        {sponsor.website_url && (
                          <span className="inline-flex items-center gap-1 mt-2 text-[10px] font-medium text-[#0C2E3C]/40 group-hover:text-[#C8E64E] transition-colors">
                            Visit <ExternalLink size={10} />
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Become a Sponsor CTA */}
        <div className="mt-20 bg-[#0C2E3C] p-8 sm:p-12 text-center" data-testid="become-sponsor-cta">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#C8E64E] mb-3">BECOME A SPONSOR</h2>
          <p className="text-[#F0F4F3]/70 text-sm max-w-xl mx-auto mb-6">
            Partner with Commonwealth FC and connect your brand with Charlottesville{"'"}s most dedicated young athletes and their families. Every dollar goes directly to player development and scholarships.
          </p>
          <Link to="/join" className="inline-block bg-[#C8E64E] text-[#0C2E3C] font-bold uppercase tracking-wider text-sm px-8 py-3 hover:bg-[#b8d43e] transition-colors">
            Get in Touch
          </Link>
        </div>
      </div>

      <footer className="bg-[#0C2E3C] mt-16 py-8 text-center">
        <p className="text-[#F0F4F3]/50 text-xs">Commonwealth FC &middot; Charlottesville, VA</p>
      </footer>
    </div>
  );
};

export default SponsorsShowcasePage;
